import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import BoxedTitle from "@/components/BoxedTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "vbb_admin_key";

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/admin/inbox";

  const [draftKey, setDraftKey] = useState("");
  const [status, setStatus] = useState("idle"); // 'idle' | 'verifying' | 'error'
  const [errorMsg, setErrorMsg] = useState("");

  // If sessionStorage already has a valid key, fast-forward through.
  useEffect(() => {
    const existing = sessionStorage.getItem(KEY_STORAGE);
    if (!existing) return;
    let cancelled = false;
    axios
      .get(`${API}/admin/verify`, { headers: { "X-Admin-Key": existing } })
      .then(() => {
        if (!cancelled) navigate(next, { replace: true });
      })
      .catch(() => {
        // stale/invalid key — clear it and let user retry
        sessionStorage.removeItem(KEY_STORAGE);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, next]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = draftKey.trim();
    if (!trimmed) return;
    setStatus("verifying");
    setErrorMsg("");
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { "X-Admin-Key": trimmed },
      });
      sessionStorage.setItem(KEY_STORAGE, trimmed);
      navigate(next, { replace: true });
    } catch (err) {
      const code = err?.response?.status;
      setStatus("error");
      setErrorMsg(
        code === 401
          ? "That key isn't valid. Try again."
          : "Could not reach the server. Try again."
      );
    }
  };

  return (
    <main
      className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-32"
      data-testid="login-page"
    >
      <div className="vbb-overline">
        <span className="vbb-dot" />
        Restricted Area
      </div>
      <h1
        className="font-display uppercase text-5xl sm:text-7xl mt-6 tracking-tighter leading-[0.95] pl-[0.35em]"
        data-testid="login-title"
      >
        <BoxedTitle size="lg" thick>
          Login.
        </BoxedTitle>
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-16">
        <div className="lg:col-span-7">
          <form
            className="border border-[#0a0a0a]"
            data-testid="login-form"
            onSubmit={onSubmit}
          >
            <label className="block p-6 vbb-border-b">
              <span className="font-mono-tiny text-[#6b6b6b]">Admin Key</span>
              <input
                type="password"
                required
                autoFocus
                autoComplete="current-password"
                value={draftKey}
                onChange={(e) => setDraftKey(e.target.value)}
                placeholder="X-Admin-Key value"
                className="mt-3 w-full bg-transparent outline-none text-[16px] text-[#0a0a0a] placeholder:text-[#9a9a9a]"
                data-testid="login-key-input"
              />
            </label>
            <div className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <span
                className="font-mono-tiny text-[#6b6b6b]"
                data-testid="login-status-text"
                aria-live="polite"
              >
                {status === "error" ? (
                  <span className="text-[#e34234]">{errorMsg}</span>
                ) : status === "verifying" ? (
                  "Verifying…"
                ) : (
                  "Stored only in this tab's sessionStorage."
                )}
              </span>
              <button
                type="submit"
                disabled={status === "verifying"}
                className="vbb-btn disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="login-submit-btn"
              >
                {status === "verifying" ? "…" : "Unlock →"}
              </button>
            </div>
          </form>
        </div>
        <aside className="lg:col-span-5">
          <div className="font-mono-tiny text-[#6b6b6b]">
            Why is this here?
          </div>
          <p className="mt-3 text-[15px] leading-relaxed text-[#2a2a2a]">
            VBB uses this area for internal tools — the contact inbox and the
            competitor tracker. Public visitors won't have a key. If you're
            looking for the company, start with{" "}
            <Link to="/" className="vbb-link" data-testid="login-home-link">
              the homepage
            </Link>{" "}
            or send us{" "}
            <Link
              to="/contact"
              className="vbb-link"
              data-testid="login-contact-link"
            >
              a note
            </Link>
            .
          </p>
        </aside>
      </div>
    </main>
  );
}
